import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  Button,
  Typography,
  Divider,
  useMediaQuery,
  TablePagination,
  Checkbox,
  Chip,
  Stack,
  Tooltip,
} from "@mui/material";
import {
  createNewTableColumns,
  memberTableColumns,
} from "../../../utils/memberUtils";
import BeltAvatar from "../../../components/beltavatar/BeltAvatar";
import { useDispatch, useSelector } from "react-redux";
import { setDialog } from "../../../features/dialog/dialogSlice";
import MemberTableTopBar from "./MemberTableTopBar";
import { displayDate, getMemberCountPerSite } from "../../../utils/dataUtils";
import MemberTablePopover from "./MemberTablePopover";
import { selectCurrentUserData } from "../../../features/auth/authSlice";
import { gradArray } from "../../../components/form/fields/inputFields";
import Iconify from "../../../components/iconify/Iconify";
import { getMemberStatusConfig } from "../../../utils/memberStatus";

const normalizeStatus = (value) => String(value || "").trim().toLowerCase();

const MemberTable = ({
  members,
  defaultStatuses,
  focusMemberId,
  focusStatus,
  focusAction,
  onClearFocus,
}) => {
  const [tableColumns, setTableColumns] = useState(memberTableColumns);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [sortInitialized, setSortInitialized] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [focusedMemberId, setFocusedMemberId] = useState(() =>
    String(focusMemberId || "").trim(),
  );

  const userData = useSelector(selectCurrentUserData);

  // DEFAULT: show only active members
  const initialStatuses = useMemo(
    () =>
      defaultStatuses && defaultStatuses.length ? defaultStatuses : ["active"],
    [defaultStatuses],
  );
  const [filters, setFilters] = useState(() => ({
    sites: [],
    degrees: [],
    statuses: initialStatuses,
  }));

  const [selectedIds, setSelectedIds] = useState([]);
  const isMobile = useMediaQuery("(max-width:600px)");
  const dispatch = useDispatch();
  const normalizedFocusStatus = normalizeStatus(focusStatus);
  const normalizedFocusAction = String(focusAction || "").trim().toLowerCase();

  useEffect(() => {
    if (isMobile) {
      setTableColumns(
        createNewTableColumns(
          memberTableColumns.map((c) => c.label),
          ["Nachname", "Vorname"],
        ),
      );
    } else {
      // Check if user has saved table column settings
      const userSettings =
        userData?.settings?.memberTableSettings?.defaultColumns;
      if (userSettings && userSettings.length > 0) {
        setTableColumns(
          createNewTableColumns(
            memberTableColumns.map((c) => c.label),
            userSettings,
          ),
        );
        return;
      }
      setTableColumns(memberTableColumns);
    }
  }, [isMobile, userData]);

  useEffect(() => {
    if (sortInitialized) return;
    const saved = userData?.settings?.memberTableSettings?.defaultSort;
    if (saved?.value) {
      setSortBy(saved.value);
      setSortOrder(saved.order || "asc");
      setSortInitialized(true);
    }
  }, [userData, sortInitialized]);

  useEffect(() => {
    setFocusedMemberId(String(focusMemberId || "").trim());
  }, [focusMemberId]);

  useEffect(() => {
    if (!focusedMemberId || !Array.isArray(members) || members.length === 0) {
      return;
    }

    const targetMember = members.find(
      (member) => String(member?._id) === focusedMemberId,
    );
    if (!targetMember) return;

    const targetStatus =
      normalizeStatus(targetMember?.status) || normalizedFocusStatus;
    const targetSiteId = String(
      targetMember?.standortRef?._id || targetMember?.standortRef || "",
    );
    const targetDegree = Number(targetMember?.degree);

    setFilters((prev) => {
      const next = {
        sites: [...(prev?.sites || [])],
        degrees: [...(prev?.degrees || [])],
        statuses:
          Array.isArray(prev?.statuses) && prev.statuses.length > 0
            ? [...prev.statuses]
            : [...initialStatuses],
      };

      let changed = false;

      if (targetStatus && !next.statuses.includes(targetStatus)) {
        next.statuses = [...next.statuses, targetStatus];
        changed = true;
      }

      if (next.sites.length > 0 && targetSiteId && !next.sites.includes(targetSiteId)) {
        next.sites = [...next.sites, targetSiteId];
        changed = true;
      }

      if (
        next.degrees.length > 0 &&
        Number.isFinite(targetDegree) &&
        !next.degrees.includes(targetDegree)
      ) {
        next.degrees = [...next.degrees, targetDegree];
        changed = true;
      }

      return changed ? next : prev;
    });

    setSearchTerm((prev) => (prev ? "" : prev));
  }, [focusedMemberId, members, normalizedFocusStatus, initialStatuses]);

  const handleChangeTableColumns = (newColumns) => setTableColumns(newColumns);

  const handleChangeDegree = (m) => {
    dispatch(setDialog({ title: "Gürtelgrad ändern", type: "EDIT", data: m }));
  };

  const handleSearchTerm = (e) => setSearchTerm(e.target.value);

  // ---------- Filter helpers ----------
  const passesSiteFilter = useCallback(
    (m) => {
      if (!filters.sites?.length) return true;
      const id = String(m?.standortRef?._id || m?.standortRef || "");
      return filters.sites.includes(id);
    },
    [filters.sites],
  );

  const passesDegreeFilter = useCallback(
    (m) => {
      if (!filters.degrees?.length) return true;
      return filters.degrees.includes(Number(m?.degree));
    },
    [filters.degrees],
  );

  const passesStatusFilter = useCallback(
    (m) => {
      const current = (m?.status || "").toLowerCase();
      const list = filters?.statuses || ["active"];
      return list.includes(current);
    },
    [filters.statuses],
  );

  const filteredMembers = useMemo(() => {
    return members
      .filter((m) =>
        Object.values(m).some((value) =>
          String(value).toLowerCase().includes(searchTerm.toLowerCase()),
        ),
      )
      .filter(passesSiteFilter)
      .filter(passesDegreeFilter)
      .filter(passesStatusFilter);
  }, [
    members,
    searchTerm,
    passesSiteFilter,
    passesDegreeFilter,
    passesStatusFilter,
  ]);

  useEffect(() => {
    const maxPage = Math.max(
      Math.ceil(
        filteredMembers.length /
          (rowsPerPage > 0 ? rowsPerPage : filteredMembers.length || 1),
      ) - 1,
      0,
    );
    if (page > maxPage) {
      setPage(0);
    }
  }, [filteredMembers.length, rowsPerPage, page]);

  const handleSort = (column, order) => {
    if (order) {
      setSortBy(column);
      setSortOrder(order);
      return;
    }
    if (sortBy === column && sortOrder === "asc") setSortOrder("desc");
    else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const sortedMembers = useMemo(() => {
    const arr = [...filteredMembers];
    if (sortBy !== "") {
      const order = sortOrder === "asc" ? 1 : -1;
      arr.sort(
        (a, b) =>
          (a[sortBy] ?? "").toString().localeCompare(b[sortBy], undefined, {
            numeric: true,
          }) * order,
      );
    }
    return arr;
  }, [filteredMembers, sortBy, sortOrder]);

  const paginatedMembers = useMemo(() => {
    if (rowsPerPage <= 0) {
      return sortedMembers;
    }
    const start = page * rowsPerPage;
    return sortedMembers.slice(start, start + rowsPerPage);
  }, [sortedMembers, page, rowsPerPage]);

  const focusedMember = useMemo(
    () =>
      members.find((member) => String(member?._id || "") === focusedMemberId) ||
      null,
    [members, focusedMemberId],
  );

  useEffect(() => {
    if (!focusedMemberId) return;
    const index = sortedMembers.findIndex(
      (member) => String(member?._id || "") === focusedMemberId,
    );
    if (index < 0 || rowsPerPage <= 0) return;

    const targetPage = Math.floor(index / rowsPerPage);
    if (targetPage !== page) {
      setPage(targetPage);
    }
  }, [focusedMemberId, sortedMembers, rowsPerPage, page]);

  useEffect(() => {
    if (!focusedMemberId) return;
    const visible = paginatedMembers.some(
      (member) => String(member?._id || "") === focusedMemberId,
    );
    if (!visible) return;

    const row = document.querySelector(
      `[data-member-row-id="${focusedMemberId}"]`,
    );
    if (row) {
      row.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [focusedMemberId, paginatedMembers]);

  // ---------- Selection ----------
  const isSelected = (id) => selectedIds.includes(id);
  const toggleRowSelection = (id) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  const clearSelection = () => setSelectedIds([]);

  const pageIds = useMemo(
    () => paginatedMembers.map((m) => m._id),
    [paginatedMembers],
  );
  const allPageSelected =
    pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
  const somePageSelected =
    !allPageSelected && pageIds.some((id) => selectedIds.includes(id));

  const handleSelectAllPage = () => {
    if (allPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...pageIds])]);
    }
  };

  // ---------- Bulk actions ----------
  const handleBulkDelete = () => {
    const selectedMembers = members.filter((m) => selectedIds.includes(m._id));
    dispatch(
      setDialog({
        title: "Mitglieder löschen",
        type: "BULK_DELETE",
        data: selectedMembers,
      }),
    );
  };
  const handleBulkAddTestingList = () => {
    const selectedMembers = members.filter((m) => selectedIds.includes(m._id));
    dispatch(
      setDialog({
        title: "Zur Prüfungsliste hinzufügen",
        type: "BULK_ADD_TO_TESTING",
        data: selectedMembers,
      }),
    );
  };
  const handleBulkAddEvent = () => {
    const selectedMembers = members.filter((m) => selectedIds.includes(m._id));
    dispatch(
      setDialog({
        title: "Zu Veranstaltung hinzufügen",
        type: "BULK_ADD_TO_EVENT",
        data: { members: selectedMembers },
      }),
    );
  };

  const handleBulkEdit = () => {
    dispatch(
      setDialog({
        title: "Mehrere Mitglieder bearbeiten",
        type: "BULK_EDIT",
        data: { memberIds: selectedIds },
      }),
    );
  };

  const handleOpenFocusedMemberDetails = () => {
    if (!focusedMember) return;
    dispatch(
      setDialog({
        title: "Mitglied",
        type: "MEMBERACTIONS",
        data: { action: "SHOW", member: focusedMember },
      }),
    );
  };

  const handleClearFocusedMember = () => {
    setFocusedMemberId("");
    onClearFocus?.();
  };

  const focusedMemberStatusConfig = focusedMember
    ? getMemberStatusConfig(focusedMember?.status)
    : null;
  const focusActionText =
    normalizedFocusAction === "reactivate"
      ? "reaktiviert"
      : normalizedFocusAction === "terminate"
      ? "gekündigt"
      : "aktualisiert";
  const focusMemberLabel = focusedMember
    ? `${focusedMember?.firstName || ""} ${focusedMember?.lastName || ""}`.trim()
    : "";

  return (
    <Paper elevation={6} style={{ backgroundColor: "#bfbfbf" }}>
      <MemberTableTopBar
        handleSort={handleSort}
        handleSearchTerm={handleSearchTerm}
        searchTerm={searchTerm}
        tableColumns={tableColumns}
        handleChangeTableColumns={handleChangeTableColumns}
        // selection & bulk actions
        selectionCount={selectedIds.length}
        onClearSelection={clearSelection}
        onBulkDelete={handleBulkDelete}
        onBulkAddTestingList={handleBulkAddTestingList}
        onBulkAddEvent={handleBulkAddEvent}
        onBulkEdit={handleBulkEdit}
        // filters
        filters={filters}
        onChangeFilters={setFilters}
      />
      {focusedMemberId ? (
        <Alert
          severity={focusedMember ? "info" : "warning"}
          icon={
            <Iconify
              icon={
                focusedMember
                  ? "mdi:target-account"
                  : "mdi:account-alert-outline"
              }
              width={20}
            />
          }
          action={
            <Stack direction="row" spacing={1}>
              {focusedMember ? (
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  onClick={handleOpenFocusedMemberDetails}
                >
                  Details öffnen
                </Button>
              ) : null}
              <Button size="small" color="inherit" onClick={handleClearFocusedMember}>
                Schließen
              </Button>
            </Stack>
          }
          sx={{
            mt: 1.5,
            mx: 2,
            borderRadius: 2,
            border: "1px solid",
            borderColor: focusedMember ? "info.light" : "warning.light",
            background:
              focusedMember
                ? "linear-gradient(135deg, rgba(14,165,233,0.08) 0%, rgba(56,189,248,0.15) 100%)"
                : "linear-gradient(135deg, rgba(245,158,11,0.1) 0%, rgba(251,191,36,0.16) 100%)",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            {focusedMember ? (
              <>
                <Typography variant="subtitle2">
                  Benachrichtigung geöffnet: {focusMemberLabel} wurde {focusActionText}.
                </Typography>
                {focusedMemberStatusConfig ? (
                  <Chip
                    size="small"
                    color={focusedMemberStatusConfig.color}
                    icon={<Iconify icon={focusedMemberStatusConfig.icon} width={14} />}
                    label={focusedMemberStatusConfig.label}
                  />
                ) : null}
              </>
            ) : (
              <Typography variant="subtitle2">
                Das verknüpfte Mitglied konnte nicht gefunden werden.
              </Typography>
            )}
          </Stack>
        </Alert>
      ) : null}
      <TableContainer sx={{ maxHeight: { xs: "65vh", lg: "70vh" } }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                padding="checkbox"
                style={{ backgroundColor: "#bfbfbf" }}
              >
                <Checkbox
                  indeterminate={somePageSelected}
                  checked={allPageSelected}
                  onChange={handleSelectAllPage}
                  sx={{
                    color: "#666",
                    "&.Mui-checked, &.MuiCheckbox-indeterminate": {
                      color: "#7c3aed",
                    },
                  }}
                />
              </TableCell>
              <TableCell style={{ backgroundColor: "#bfbfbf" }}>
                Aktionen
              </TableCell>
              {tableColumns.map(
                (tc) =>
                  tc?.show && (
                    <TableCell
                      style={{ backgroundColor: "#bfbfbf" }}
                      key={tc.label}
                    >
                      <TableSortLabel
                        active={sortBy === tc.value}
                        direction={sortOrder}
                        onClick={() => handleSort(tc.value)}
                      >
                        {tc.label}
                      </TableSortLabel>
                    </TableCell>
                  ),
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedMembers.map((member) => (
              <MemberTableRow
                key={member._id}
                member={member}
                onChangeDegree={handleChangeDegree}
                tableColumns={tableColumns}
                selected={isSelected(member._id)}
                onToggle={() => toggleRowSelection(member._id)}
                focused={String(member?._id || "") === focusedMemberId}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={sortedMembers.length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          const value = parseInt(event.target.value, 10);
          setRowsPerPage(value);
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, { label: "Alle", value: -1 }]}
        labelRowsPerPage="Einträge pro Seite"
      />
      <div>
        <Divider />
        <Typography textAlign={"center"} variant="h6" sx={{ p: 1 }}>
          Gesamt: {filteredMembers.length}
          {getMemberCountPerSite(filteredMembers).map(
            ({ siteName, count }) => ` | ${siteName}: ${count}`,
          )}
        </Typography>
      </div>
    </Paper>
  );
};

MemberTable.propTypes = {
  members: PropTypes.array.isRequired,
  defaultStatuses: PropTypes.array,
  focusMemberId: PropTypes.string,
  focusStatus: PropTypes.string,
  focusAction: PropTypes.string,
  onClearFocus: PropTypes.func,
};

export default MemberTable;

const MemberTableRow = ({
  member,
  onChangeDegree,
  tableColumns,
  selected,
  focused,
  onToggle,
}) => {
  const displayMemberCells = () => {
    let memberCells = tableColumns.map((c, i) => {
      if (!c?.show) return null;
      let cell = member[c.value] ?? "";

      if (c.type === "date") {
        cell = displayDate(member[c.value]);
      }

      if (c.value === "standortRef") {
        cell = member[c.value]?.siteName;
      }

      if (c.value === "status") {
        cell = <MemberStatusButton member={member} />;
      }

      if (c.value === "belt") {
        cell = (
          <Button onClick={() => onChangeDegree(member)} sx={{ m: 0, p: 0 }}>
            <BeltAvatar
              beltNumber={member["degree"]}
              size="medium"
              onlyBelt
              justify={"flex-start"}
            />
          </Button>
        );
      }

      if (c.value === "degree") {
        cell = gradArray[member[c.value]]?.label ?? "";
      }

      return <TableCell key={c.value + "-" + i}>{cell}</TableCell>;
    });
    return memberCells;
  };

  return (
    <TableRow
      hover
      selected={selected}
      data-member-row-id={String(member?._id || "")}
      onClick={onToggle}
      sx={{
        "&:hover": { cursor: "pointer" },
        ...(selected && {
          background:
            "linear-gradient(135deg, rgba(124,58,237,0.08) 0%, rgba(99,102,241,0.12) 100%)",
          "& .MuiTableCell-root": {
            borderBottom: "1px solid rgba(124,58,237,0.15)",
          },
        }),
        ...(focused && {
          background:
            "linear-gradient(135deg, rgba(245,158,11,0.12) 0%, rgba(251,191,36,0.22) 100%)",
          boxShadow: "inset 4px 0 0 rgba(217,119,6,0.95)",
          "& .MuiTableCell-root": {
            borderBottom: "1px solid rgba(217,119,6,0.3)",
          },
          animation: "memberFocusPulse 1.6s ease-in-out 0s 2",
          "@keyframes memberFocusPulse": {
            "0%": {
              boxShadow: "inset 4px 0 0 rgba(217,119,6,0.7)",
            },
            "50%": {
              boxShadow:
                "inset 4px 0 0 rgba(217,119,6,1), 0 0 0 2px rgba(251,191,36,0.22)",
            },
            "100%": {
              boxShadow: "inset 4px 0 0 rgba(217,119,6,0.7)",
            },
          },
        }),
      }}
      key={member.mitgliedId}
    >
      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
        <Checkbox
          checked={selected}
          onChange={onToggle}
          sx={{
            color: "#999",
            "&.Mui-checked": {
              color: "#7c3aed",
            },
          }}
        />
      </TableCell>
      <TableCell align="left" onClick={(e) => e.stopPropagation()}>
        <MemberTablePopover member={member} />
      </TableCell>
      {displayMemberCells()}
    </TableRow>
  );
};

MemberTableRow.propTypes = {
  member: PropTypes.object.isRequired,
  onChangeDegree: PropTypes.func.isRequired,
  tableColumns: PropTypes.array.isRequired,
  selected: PropTypes.bool,
  focused: PropTypes.bool,
  onToggle: PropTypes.func,
};

const MemberStatusButton = ({ member }) => {
  const dispatch = useDispatch();
  const handleChangeStatus = () => {
    dispatch(
      setDialog({
        title: "Status ändern",
        type: "EDIT",
        content: "edit-status",
        data: member,
      }),
    );
  };
  const statusConfig = getMemberStatusConfig(member?.status);

  return (
    <Tooltip title="Status ändern">
      <Chip
        clickable
        color={statusConfig.color}
        icon={<Iconify icon={statusConfig.icon} width={16} />}
        label={statusConfig.label}
        onClick={(event) => {
          event.stopPropagation();
          handleChangeStatus();
        }}
        sx={{
          borderRadius: "10px",
          fontWeight: 600,
          minWidth: 70,
          justifyContent: "flex-start",
          px: 0.5,
          "& .MuiChip-label": {
            px: 1,
          },
        }}
      />
    </Tooltip>
  );
};
